import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { authUrl } from '@/lib/config'
import { AuthContext, type AuthContextValue } from './authContext'
import {
  getStoredToken,
  storeToken,
  clearStoredToken,
  extractTokenFromUrl,
  removeTokenFromUrl,
} from './tokenUtils'
import { configureAuthFetch } from './authFetch'

interface AuthProviderProps {
  validateToken: (token: string) => Promise<boolean>
  children: ReactNode
}

export function AuthProvider({ validateToken, children }: AuthProviderProps) {
  const [token, setTokenState] = useState<string | null>(null)
  const [hasAccess, setHasAccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const tokenRef = useRef<string | null>(null)

  const setToken = useCallback((newToken: string | null) => {
    tokenRef.current = newToken
    setTokenState(newToken)
    if (newToken) {
      storeToken(newToken)
    } else {
      clearStoredToken()
    }
  }, [])

  const markHasAccess = useCallback(() => setHasAccess(true), [])
  const markNoAccess = useCallback(() => {
    setHasAccess(false)
    setToken(null)
  }, [setToken])

  const redirectToLogin = useCallback(() => {
    const base = authUrl()
    if (!base) {
      console.error('VITE_AUTH_URL is not configured. Cannot redirect to login.')
      return
    }
    const redirectUrl = encodeURIComponent(window.location.origin + window.location.pathname)
    window.location.href = `${base}?redirectUrl=${redirectUrl}`
  }, [])

  // Init: extract token from URL or restore from sessionStorage, then validate
  useEffect(() => {
    let cancelled = false

    async function init() {
      let currentToken = extractTokenFromUrl()
      if (currentToken) {
        storeToken(currentToken)
        removeTokenFromUrl()
      } else {
        currentToken = getStoredToken()
      }

      if (!currentToken) {
        setIsLoading(false)
        return
      }

      tokenRef.current = currentToken
      setTokenState(currentToken)

      try {
        const isValid = await validateToken(currentToken)
        if (cancelled) return
        if (isValid) {
          markHasAccess()
        } else {
          markNoAccess()
        }
      } catch {
        if (!cancelled) markNoAccess()
      }

      if (!cancelled) setIsLoading(false)
    }

    init()
    return () => { cancelled = true }
  }, [validateToken, markHasAccess, markNoAccess])

  // Wire up authFetch with current token ref and auth failure handler
  useEffect(() => {
    configureAuthFetch(
      () => tokenRef.current,
      () => markNoAccess(),
    )
  }, [markNoAccess])

  const value = useMemo<AuthContextValue>(() => ({
    token,
    hasAccess,
    isLoading,
    setToken,
    markHasAccess,
    markNoAccess,
    redirectToLogin,
  }), [token, hasAccess, isLoading, setToken, markHasAccess, markNoAccess, redirectToLogin])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
